import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UpdateAppointmentActionType } from '../proxies/AppointmentProxy';
import { ProfilePreferenceTypeEnum } from '../proxies/UserProxy';
import { updateAppointmentRequest } from '../redux/ducks/AppointmentDucks';
import { setMessageWithActionTimestamp } from '../redux/ducks/ChatDucks';
import type { ConversationMessageDataType } from '../redux/ducks/ChatDucks.types';
import { setPushPreference } from '../redux/ducks/StorageDucks';
import { setChatModifyAppointmentModalOpen } from '../redux/ducks/UIDucks';
import { ActionModalActionType } from '../redux/ducks/UIDucks.types';
import { userUpdateProfilePreferenceRequest } from '../redux/ducks/UserDucks';
import type { RootReducerType } from '../redux/reducers/RootReducer.types';
import useActionModal from './useActionModal';

interface ChatMessageActionsStore {
  modifyAppointment: () => void;
  confirmAppointment: () => void;
  denyAppointment: () => void;
  cancelNewAppointmentRequest: () => void;
  requestCancelAppointment: () => void;
  acceptCancelAppointment: () => void;
  denyCancelAppointment: () => void;
  acceptChangeAppointment: () => void;
  denyChangeAppointment: () => void;
  cancelChangeAppointmentRequest: () => void;
  cancelCancelAppointmentRequest: () => void;
  denyPushNotifications: () => void;
}

const useChatMessageActions = (): ChatMessageActionsStore => {
  const dispatch = useDispatch();
  const actionModal = useActionModal();
  const { t } = useTranslation();

  const { currentConversation, messages } = useSelector(
    (state: RootReducerType) => state.chat,
  );

  const { onActionModalConfirm, actionModalProps } = useSelector(
    (state: RootReducerType) => state.ui,
  );

  const statusMessage = useMemo<ConversationMessageDataType | undefined>(() => {
    if (messages?.status) {
      return messages?.status.data;
    }

    return undefined;
  }, [messages]);

  useEffect(() => {
    if (actionModal.isOpen) {
      if (
        currentConversation &&
        statusMessage &&
        statusMessage.meta?.current_appointment &&
        onActionModalConfirm
      ) {
        let shouldRefreshChat = false;
        if (
          actionModalProps?.action === ActionModalActionType.ConfirmAppointment
        ) {
          console.log('Confirmed to confirm appointment', statusMessage);
          dispatch(
            updateAppointmentRequest({
              appointment: statusMessage.meta.current_appointment,
              action: UpdateAppointmentActionType.ConfirmNew,
            }),
          );
          shouldRefreshChat = true;
        } else if (
          actionModalProps?.action === ActionModalActionType.DenyAppointment
        ) {
          console.log('Confirmed to deny appointment', statusMessage);
          dispatch(
            updateAppointmentRequest({
              appointment: statusMessage.meta.current_appointment,
              action: UpdateAppointmentActionType.DenyNew,
            }),
          );
          shouldRefreshChat = true;
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.CancelNewAppointment
        ) {
          console.log('Confirmed to cancel new appointment', statusMessage);
          dispatch(
            updateAppointmentRequest({
              appointment: statusMessage.meta.current_appointment,
              action: UpdateAppointmentActionType.CancelNew,
            }),
          );
          shouldRefreshChat = true;
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.RequestCancelAppointment
        ) {
          console.log('Confirmed to request cancel appointment', statusMessage);
          dispatch(
            updateAppointmentRequest({
              appointment: statusMessage.meta.current_appointment,
              action: UpdateAppointmentActionType.CancelExisting,
            }),
          );
          shouldRefreshChat = true;
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.AcceptCancelAppointment
        ) {
          console.log('Confirmed to accept cancel appointment', statusMessage);
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.DenyCancelAppointment
        ) {
          console.log('Confirmed to deny cancel appointment', statusMessage);
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.AcceptChangeAppointment
        ) {
          console.log('Confirmed to accept change appointment', statusMessage);
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.DenyChangeAppointment
        ) {
          console.log('Confirmed to deny change appointment', statusMessage);
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.CancelChangeAppointment
        ) {
          console.log('Confirmed to cancel change appointment', statusMessage);
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.CancelCancelAppointment
        ) {
          console.log('Confirmed to cancel cancel appointment', statusMessage);
        } else if (
          actionModalProps?.action ===
          ActionModalActionType.DenyPushNotifications
        ) {
          dispatch(setPushPreference({ enable: false, updated: Date.now() }));
          dispatch(
            userUpdateProfilePreferenceRequest({
              type: ProfilePreferenceTypeEnum.UserPushNotification,
              value: '0',
            }),
          );
        }

        if (shouldRefreshChat) {
          dispatch(setMessageWithActionTimestamp(statusMessage.timestamp));
        }
      }
    }
  }, [onActionModalConfirm]);

  const modifyAppointment = () => {
    dispatch(setChatModifyAppointmentModalOpen(true));
  };

  const confirmAppointment = () => {
    console.log('Confirm appointment');
    actionModal.onOpen({
      action: ActionModalActionType.ConfirmAppointment,
      message: t('static.chat.confirm_new_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.confirm_new_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.confirm_new_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'accept',
    });
  };

  const denyAppointment = () => {
    console.log('Deny appointment');
    actionModal.onOpen({
      action: ActionModalActionType.DenyAppointment,
      message: t('static.chat.deny_new_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.deny_new_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.deny_new_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  const cancelNewAppointmentRequest = () => {
    console.log('Cancel new appointment request');
    actionModal.onOpen({
      action: ActionModalActionType.CancelNewAppointment,
      message: t('static.chat.cancel_new_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.cancel_new_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.cancel_new_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  const requestCancelAppointment = () => {
    console.log('Request cancel appointment');
    actionModal.onOpen({
      action: ActionModalActionType.RequestCancelAppointment,
      message: t('static.chat.request_cancel_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.request_cancel_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.request_cancel_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  const acceptCancelAppointment = () => {
    console.log('Accept cancel appointment');
    actionModal.onOpen({
      action: ActionModalActionType.AcceptCancelAppointment,
      message: t('static.chat.accept_cancel_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.accept_cancel_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.accept_cancel_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'accept',
    });
  };

  const denyCancelAppointment = () => {
    console.log('Deny cancel appointment');
    actionModal.onOpen({
      action: ActionModalActionType.DenyCancelAppointment,
      message: t('static.chat.deny_cancel_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.deny_cancel_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.deny_cancel_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  const acceptChangeAppointment = () => {
    console.log('Accept change appointment');
    actionModal.onOpen({
      action: ActionModalActionType.AcceptChangeAppointment,
      message: t('static.chat.accept_change_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.accept_change_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.accept_change_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'accept',
    });
  };

  const denyChangeAppointment = () => {
    console.log('Deny change appointment');
    actionModal.onOpen({
      action: ActionModalActionType.DenyChangeAppointment,
      message: t('static.chat.deny_change_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.deny_change_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.deny_change_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  const cancelChangeAppointmentRequest = () => {
    console.log('Cancel change appointment request');
    actionModal.onOpen({
      action: ActionModalActionType.CancelChangeAppointment,
      message: t('static.chat.cancel_change_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.cancel_change_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.cancel_change_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  const cancelCancelAppointmentRequest = () => {
    console.log('Cancel cancel appointment request');
    actionModal.onOpen({
      action: ActionModalActionType.CancelCancelAppointment,
      message: t('static.chat.cancel_cancel_appointment_disclaimer_text'),
      cancelLabel: t(
        'static.chat.cancel_cancel_appointment_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.cancel_cancel_appointment_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  const denyPushNotifications = () => {
    console.log('Deny push notifications');
    actionModal.onOpen({
      action: ActionModalActionType.DenyPushNotifications,
      message: t('static.chat.push_notification_deny_disclaimer_text'),
      cancelLabel: t(
        'static.chat.push_notification_deny_disclaimer_cancel_label',
      ),
      confirmLabel: t(
        'static.chat.push_notification_deny_disclaimer_confirm_label',
      ),
      confirmLabelType: 'reject',
    });
  };

  return {
    modifyAppointment,
    confirmAppointment,
    denyAppointment,
    cancelNewAppointmentRequest,
    requestCancelAppointment,
    acceptCancelAppointment,
    denyCancelAppointment,
    acceptChangeAppointment,
    denyChangeAppointment,
    cancelChangeAppointmentRequest,
    cancelCancelAppointmentRequest,
    denyPushNotifications,
  };
};

export default useChatMessageActions;
